import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import apiService from "../../services/api";

const SubscriptionFeaturesManagement = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [dialogMode, setDialogMode] = useState("create"); // 'create' or 'edit'
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [dialogError, setDialogError] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(""); // '' = All
  const [statusFilter, setStatusFilter] = useState("active"); // active | inactive | all

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    is_active: true,
  });

  const categories = [
    "Core",
    "Users",
    "Billing",
    "Communication",
    "Analytics",
    "Integration",
    "Data",
    "Security",
    "Custom",
  ];

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(
        "/superadmin/subscriptions/features"
      );
      if (response.success) {
        setFeatures(response.data.features || []);
      } else {
        setError("Failed to fetch subscription features");
      }
    } catch (err) {
      setError("Error fetching features: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredFeatures = useMemo(() => {
    let list = Array.isArray(features) ? features : [];

    if (categoryFilter) {
      list = list.filter((f) => (f?.category || "") === categoryFilter);
    }

    if (statusFilter !== "all") {
      const wantActive = statusFilter === "active";
      list = list.filter(
        (f) => String(f?.is_active) === (wantActive ? "1" : "0")
      );
    }

    return list;
  }, [features, categoryFilter, statusFilter]);

  const handleOpenDialog = (mode, feature = null) => {
    setDialogMode(mode);
    setDialogError("");
    setFormErrors({});
    if (mode === "edit" && feature) {
      setEditingFeature(feature);
      setFormData({
        name: feature.name,
        description: feature.description,
        category: feature.category,
        is_active: feature.is_active === 1,
      });
    } else {
      setEditingFeature(null);
      setFormData({
        name: "",
        description: "",
        category: "",
        is_active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFeature(null);
    setFormData({
      name: "",
      description: "",
      category: "",
      is_active: true,
    });
    setFormErrors({});
    setDialogError("");
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "is_active" ? checked : value,
    }));
    // Clear field-level error as user edits
    setFormErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    // Clear dialog-level error while editing
    if (dialogError) setDialogError("");
  };

  const handleSubmit = async () => {
    const name = (formData.name || "").trim();
    const description = (formData.description || "").trim();
    const category = (formData.category || "").trim();

    const errors = {};
    if (!name) errors.name = "Feature name is required";
    if (!description) errors.description = "Description is required";
    if (!category) errors.category = "Category is required";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setDialogError("Please fill all required fields");
      return;
    }

    try {
      setSaving(true);
      setDialogError("");
      const payload = {
        ...formData,
        name,
        description,
        category,
        is_active: formData.is_active ? 1 : 0,
      };

      let response;
      if (dialogMode === "create") {
        response = await apiService.post(
          "/superadmin/subscriptions/features",
          payload
        );
      } else {
        response = await apiService.put(
          `/superadmin/subscriptions/features/${editingFeature.id}`,
          payload
        );
      }

      if (response.success) {
        handleCloseDialog();
        fetchFeatures();
      } else {
        const msg = response.message || "Failed to save feature";
        setDialogError(
          /duplicate entry/i.test(msg) ? "Feature name already exists." : msg
        );
      }
    } catch (err) {
      const msg = err?.message || "Failed to save feature";
      setDialogError(
        /duplicate entry/i.test(msg) ? "Feature name already exists." : msg
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFeature = async (featureId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this feature? This action cannot be undone."
      )
    ) {
      try {
        const response = await apiService.delete(
          `/superadmin/subscriptions/features/${featureId}`
        );
        if (response.success) {
          fetchFeatures();
        } else {
          setError(response.message || "Failed to delete feature");
        }
      } catch (err) {
        setError("Error deleting feature: " + err.message);
      }
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      Core: "primary",
      Users: "success",
      Billing: "warning",
      Communication: "info",
      Analytics: "secondary",
      Integration: "default",
      Data: "error",
      Security: "error",
      Custom: "default",
    };
    return colors[category] || "default";
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Subscription Features Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog("create")}
        >
          Create New Feature
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Showing <strong>{filteredFeatures.length}</strong> of{" "}
              <strong>{features.length}</strong>
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Features Table */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFeatures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No features found for the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredFeatures.map((feature) => (
                  <TableRow key={feature.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="medium">
                        {feature.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {feature.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={feature.category}
                        color={getCategoryColor(feature.category)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={feature.is_active == 1 ? "Active" : "Inactive"}
                        color={feature.is_active == 1 ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {new Date(feature.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit Feature">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog("edit", feature)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      {/* <Tooltip title="Delete Feature">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteFeature(feature.id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip> */}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === "create" ? "Create New Feature" : "Edit Feature"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {dialogError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {dialogError}
              </Alert>
            ) : null}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Feature Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  error={!!formErrors.name}
                  helperText={formErrors.name || ""}
                />
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    label="Category"
                    error={!!formErrors.category}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.category && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 1.75, display: "block" }}
                    >
                      {formErrors.category}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                  }
                  label="Active"
                />
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={6}
                  required
                  error={!!formErrors.description}
                  helperText={formErrors.description || ""}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {dialogMode === "create" ? "Create" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionFeaturesManagement;
